import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, Select, Input, Button, DatePicker, InputNumber, Space, Tag, Timeline, Row, Col, message, Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SearchOutlined, LockOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import GcPagina from '../components/Layout/GcPagina';
import { FctEstado, FctKpi, FctTarjeta, fmtFechaHora, fmtNumero, type FctFamilia } from '../fct/react';
import {
  getEnvios, getResumen, getCronologia, ESTADOS_PACTO, CANALES_PACTO, fmtDuracionMinutos,
  PactoEnvioEstado, PactoCronologia, PactosResumen, SeguimientoPactosFiltro,
} from '../api/seguimientoPactosApi';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Panel «Seguimiento de pactos» (Pacto de Firma por WhatsApp). Solo lectura y solo Sistemas: la ruta lleva
 * `RequireResource` y el backend deniega con 403 a cualquier otro (la ACL es el control; esto es usabilidad).
 *
 * El listado no tiene datos personales (código de cliente, nunca nombre ni teléfono). Al desplegar una fila, la
 * cronología sí lleva el nombre del cliente y exige su propio recurso: si responde 403 se muestra como lo que es.
 */

/** Enviado → proceso (en curso, no es un error); Aceptado → éxito; Declinado → riesgo. */
const FAMILIA_ESTADO: Record<number, FctFamilia> = { 1: 'proceso', 2: 'exito', 3: 'riesgo' };

const TINTA_CRONOLOGIA = 'var(--fct-marca-verde-oscuro)';

const formatear = (iso?: string | null): string => (iso ? fmtFechaHora(dayjs(iso).toDate()) : '—');

type EstadoCronologia =
  | { tipo: 'cargando' }
  | { tipo: 'ok'; datos: PactoCronologia }
  | { tipo: 'prohibido' }
  | { tipo: 'error' };

const CronologiaFila: React.FC<{ idPactoEnvio: number }> = ({ idPactoEnvio }) => {
  const [estado, setEstado] = useState<EstadoCronologia>({ tipo: 'cargando' });

  useEffect(() => {
    let cancelado = false;
    setEstado({ tipo: 'cargando' });
    getCronologia(idPactoEnvio)
      .then((datos) => { if (!cancelado) setEstado({ tipo: 'ok', datos }); })
      .catch((err: unknown) => {
        if (cancelado) return;
        const status = err instanceof AxiosError ? err.response?.status : undefined;
        setEstado(status === 403 ? { tipo: 'prohibido' } : { tipo: 'error' });
      });
    return () => { cancelado = true; };
  }, [idPactoEnvio]);

  if (estado.tipo === 'cargando') return <Spin size="small" />;

  if (estado.tipo === 'prohibido') {
    return (
      <Space>
        <LockOutlined />
        <Text type="secondary">La cronología con el nombre del cliente no está disponible para tu perfil.</Text>
      </Space>
    );
  }

  if (estado.tipo === 'error') {
    return <Text type="danger">No se pudo cargar la cronología. Vuelve a intentarlo.</Text>;
  }

  const { datos } = estado;
  return (
    <div style={{ padding: '8px 24px' }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong>{datos.cliente || 'Cliente sin nombre'}</Text>{' '}
        {datos.codigoCliente && <Text type="secondary" className="fct-mono">{datos.codigoCliente}</Text>}
        <div>
          <Text type="secondary">
            {datos.ccl ?? '—'}{datos.aliasOperacion ? ` · ${datos.aliasOperacion}` : ''}
          </Text>
        </div>
      </div>

      {/* Un envío, una rama: el pedido y los demás del mismo cliente y operación. En una sola tinta. */}
      <Timeline
        items={[
          ...datos.envios.flatMap((e) => [
            {
              color: TINTA_CRONOLOGIA,
              children: (
                <span>
                  <Text strong={e.esSeleccionado}>Envío {e.canal}</Text>{' '}
                  <Text type="secondary" className="fct-mono">#{e.referencia} · {formatear(e.fechaEnvio)}</Text>
                  {e.esSeleccionado && <Tag style={{ marginLeft: 8 }}>Este envío</Tag>}
                </span>
              ),
            },
            ...(e.fechaRespuesta ? [{
              color: TINTA_CRONOLOGIA,
              children: (
                <span>
                  <FctEstado personalizado={{ etiqueta: e.estado, familia: FAMILIA_ESTADO[e.idEstado] ?? 'neutro' }} />{' '}
                  <Text type="secondary" className="fct-mono">
                    {formatear(e.fechaRespuesta)} · a los {fmtDuracionMinutos(e.minutosHastaRespuesta)}
                  </Text>
                </span>
              ),
            }] : []),
          ]),
          ...(datos.fechaPacto ? [{
            color: TINTA_CRONOLOGIA,
            children: (
              <span>
                <Text strong>Pacto firmado</Text>{' '}
                <Text type="secondary" className="fct-mono">{formatear(datos.fechaPacto)}</Text>
              </span>
            ),
          }] : []),
        ]}
      />
      {datos.envios.length > 1 && (
        <Text type="secondary">Hay {datos.envios.length} envíos para este cliente y esta operación.</Text>
      )}
    </div>
  );
};

const PAGINA_POR_DEFECTO = 25;

const SeguimientoPactosPage: React.FC = () => {
  const [items, setItems] = useState<PactoEnvioEstado[]>([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState<PactosResumen | null>(null);
  const [loading, setLoading] = useState(false);

  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState(PAGINA_POR_DEFECTO);
  const [estado, setEstado] = useState<number | undefined>();
  const [canal, setCanal] = useState<number | undefined>();
  const [idAsesor, setIdAsesor] = useState<number | undefined>();
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [sinRespuestaDias, setSinRespuestaDias] = useState<number | null>(null);
  const [operacion, setOperacion] = useState('');
  /** Texto ya confirmado (Enter o borrado): el filtro va al servidor, no se dispara en cada tecla. */
  const [operacionBuscada, setOperacionBuscada] = useState('');

  // Asesores conocidos: se derivan de lo cargado (no hay catálogo aparte). Se acumulan para no perder opciones al filtrar.
  const [asesores, setAsesores] = useState<Map<number, string>>(new Map());

  const filtro: SeguimientoPactosFiltro = useMemo(() => ({
    estado,
    canal,
    idAsesor,
    desde: periodo?.[0]?.startOf('day').toISOString(),
    hasta: periodo?.[1]?.endOf('day').toISOString(),
    sinRespuestaMasDeDias: sinRespuestaDias ?? undefined,
    operacion: operacionBuscada || undefined,
  }), [estado, canal, idAsesor, periodo, sinRespuestaDias, operacionBuscada]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [lista, kpis] = await Promise.all([
        getEnvios({ ...filtro, pagina, tamano }),
        getResumen(filtro),
      ]);
      setItems(lista.items);
      setTotal(lista.total);
      setResumen(kpis);
      setAsesores((prev) => {
        const siguiente = new Map(prev);
        lista.items.forEach((f) => { if (f.idAsesor != null && f.asesor) siguiente.set(f.idAsesor, f.asesor); });
        return siguiente;
      });
    } catch {
      message.error('No se pudo cargar el seguimiento de pactos');
    } finally {
      setLoading(false);
    }
  }, [filtro, pagina, tamano]);

  useEffect(() => { cargar(); }, [cargar]);

  // Cualquier cambio de filtro vuelve a la primera página.
  const conFiltro = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };

  const columnas: ColumnsType<PactoEnvioEstado> = [
    {
      title: 'Ref.',
      dataIndex: 'referencia',
      width: 100,
      className: 'fct-mono',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Fecha de envío',
      dataIndex: 'fechaEnvio',
      width: 160,
      className: 'fct-num',
      align: 'right',
      render: (v: string) => formatear(v),
    },
    {
      title: 'Canal',
      dataIndex: 'canal',
      width: 100,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 130,
      render: (v: string, fila) => (
        <FctEstado personalizado={{ etiqueta: v, familia: FAMILIA_ESTADO[fila.idEstado] ?? 'neutro' }} />
      ),
    },
    {
      title: 'Tiempo hasta la respuesta',
      width: 190,
      align: 'right',
      className: 'fct-num',
      // Sin respuesta: los días que lleva esperando. Una aceptación tardía (días) es normal, no un error.
      render: (_: unknown, fila) => (fila.fechaRespuesta
        ? fmtDuracionMinutos(fila.minutosHastaRespuesta)
        : <Text type="secondary">{fila.diasSinRespuesta != null ? `sin respuesta · ${fila.diasSinRespuesta} d` : '—'}</Text>),
    },
    {
      title: 'Operación',
      width: 240,
      render: (_: unknown, fila) => (
        <>
          <span className="fct-mono">{fila.ccl ?? '—'}</span>
          {fila.aliasOperacion && <><br /><Text type="secondary">{fila.aliasOperacion}</Text></>}
        </>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'codigoCliente',
      width: 110,
      className: 'fct-mono',
      render: (v?: string | null) => v ?? '—',
    },
    {
      title: 'Asesor',
      dataIndex: 'asesor',
      width: 180,
      render: (v?: string | null) => v ?? '—',
    },
    {
      title: 'Pacto',
      dataIndex: 'pactoFirmado',
      width: 130,
      render: (v: boolean, fila) => (v
        ? <FctEstado personalizado={{ etiqueta: 'Firmado', familia: 'exito' }} />
        : <Text type="secondary">{fila.idEstado === 3 ? 'Declinado' : 'Sin pacto'}</Text>),
    },
    {
      title: 'Envíos',
      dataIndex: 'enviosMismoPacto',
      width: 90,
      align: 'right',
      className: 'fct-num',
      // Más de uno para el mismo cliente y operación = doble envío. Se marca en neutro: es un aviso, no un fallo.
      render: (v: number) => (v > 1 ? <Tag>{v} envíos</Tag> : v),
    },
  ];

  const kpis = resumen;

  return (
    <GcPagina
      seccion="Pactos"
      titulo="Seguimiento de pactos"
      densidad="compacta"
      acciones={<Button icon={<ReloadOutlined />} onClick={cargar} loading={loading}>Actualizar</Button>}
    >
      {kpis && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={8} xl={6}><FctKpi etiqueta="Enviados" valor={fmtNumero(kpis.enviados)} /></Col>
          <Col xs={12} md={8} xl={6}><FctKpi etiqueta="Aceptados" valor={fmtNumero(kpis.aceptados)} /></Col>
          <Col xs={12} md={8} xl={6}><FctKpi etiqueta="Declinados" valor={fmtNumero(kpis.declinados)} /></Col>
          <Col xs={12} md={8} xl={6}><FctKpi etiqueta="Pendientes" valor={fmtNumero(kpis.pendientes)} /></Col>
          <Col xs={12} md={8} xl={6}>
            <FctKpi
              etiqueta={`Pendientes > ${kpis.umbralDias} días`}
              valor={fmtNumero(kpis.pendientesAntiguos)}
              nota="Una aceptación tardía es normal"
            />
          </Col>
          <Col xs={12} md={8} xl={6}>
            <FctKpi etiqueta="Tiempo medio de aceptación" valor={fmtDuracionMinutos(kpis.minutosMediaAceptacion)} />
          </Col>
          <Col xs={12} md={8} xl={6}>
            <FctKpi etiqueta="Mediana de aceptación" valor={fmtDuracionMinutos(kpis.minutosMedianaAceptacion)} />
          </Col>
          <Col xs={12} md={8} xl={6}>
            <FctKpi
              etiqueta="Clientes con doble envío"
              valor={fmtNumero(kpis.clientesConDobleEnvio)}
              nota="Más de un envío para la misma operación"
            />
          </Col>
        </Row>
      )}

      <FctTarjeta>
        <div className="fct-filtros" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="fct-filtros">
            <Select
              allowClear placeholder="Estado" style={{ width: 150 }} value={estado}
              onChange={conFiltro(setEstado)} options={ESTADOS_PACTO}
            />
            <Select
              allowClear placeholder="Canal" style={{ width: 130 }} value={canal}
              onChange={conFiltro(setCanal)} options={CANALES_PACTO}
            />
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="Asesor" style={{ width: 200 }} value={idAsesor}
              onChange={conFiltro(setIdAsesor)}
              options={Array.from(asesores.entries())
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, nombre]) => ({ value: id, label: nombre }))}
            />
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Envío desde', 'Envío hasta']}
              value={periodo}
              onChange={(v) => { setPeriodo(v as [Dayjs | null, Dayjs | null] | null); setPagina(1); }}
            />
            <InputNumber
              min={0} max={365} placeholder="Sin respuesta > N días" style={{ width: 190 }}
              value={sinRespuestaDias}
              onChange={(v) => { setSinRespuestaDias(v); setPagina(1); }}
            />
          </div>
          <Input
            placeholder="CCL o alias de la operación"
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            allowClear
            maxLength={80}
            value={operacion}
            onChange={(e) => {
              setOperacion(e.target.value);
              if (!e.target.value) { setOperacionBuscada(''); setPagina(1); }
            }}
            onPressEnter={() => { setOperacionBuscada(operacion.trim()); setPagina(1); }}
          />
        </div>

        <Table<PactoEnvioEstado>
          rowKey="idPactoEnvio"
          columns={columnas}
          dataSource={items}
          // Velo de carga solo la primera vez; al refrescar con datos en pantalla la tabla no parpadea.
          loading={loading && items.length === 0}
          size="small"
          scroll={{ x: 1300 }}
          pagination={{
            current: pagina,
            pageSize: tamano,
            total,
            showSizeChanger: true,
            showTotal: (t) => `${t} envíos`,
            onChange: (p, s) => { setPagina(p); setTamano(s); },
          }}
          expandable={{ expandedRowRender: (fila) => <CronologiaFila idPactoEnvio={fila.idPactoEnvio} /> }}
        />
      </FctTarjeta>
    </GcPagina>
  );
};

export default SeguimientoPactosPage;
